import { memo, type ReactNode } from "react";
import Tooltip, { tooltipClasses, type TooltipProps } from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { fieldLabel } from "../../../../../lib/scorecardFieldUtils";
import {
  RECOGNIZER_BY_SLUG,
  type EvidenceTokenCategory,
  type EvidenceTokenSlug,
} from "./recognizers";

const TokenMark = styled("span")<{ $category: EvidenceTokenCategory }>(({ theme, $category }) => ({
  borderRadius: 3,
  padding: "0 2px",
  fontWeight: 600,
  cursor: "help",
  whiteSpace: "nowrap",
  color: theme.evidenceTokens[$category].color,
  backgroundColor: theme.evidenceTokens[$category].background,
}));

const RichTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.grey[900],
    maxWidth: 260,
    padding: theme.spacing(1, 1.25),
    borderRadius: 6,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.grey[900],
  },
}));

const CategorySwatch = styled("span")<{ $category: EvidenceTokenCategory }>(
  ({ theme, $category }) => ({
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.evidenceTokens[$category].color,
  })
);

const TokenHeading = styled(Typography)({
  fontWeight: 700,
  textTransform: "capitalize",
  lineHeight: 1.3,
});

interface Props {
  slug: EvidenceTokenSlug;
  children: ReactNode;
}

/**
 * Renders a recognised evidence token inline: a category-coloured mark with a rich hover tooltip
 * that names the token and the scorecard field(s) it is evidence for.
 */
export const EvidenceTokenMark = memo(function EvidenceTokenMark({ slug, children }: Props) {
  const recognizer = RECOGNIZER_BY_SLUG[slug];
  const fields = recognizer.fields.map(fieldLabel).join(", ");

  const tooltip = (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <CategorySwatch $category={recognizer.category} />
        <TokenHeading variant="caption">Possible {recognizer.label}</TokenHeading>
      </Stack>
      <Typography variant="caption" color="grey.400">
        Evidence for {fields}
      </Typography>
    </Stack>
  );

  return (
    <RichTooltip title={tooltip} enterDelay={200} disableInteractive>
      <TokenMark $category={recognizer.category} data-evidence-token={slug}>
        {children}
      </TokenMark>
    </RichTooltip>
  );
});
